import { useTranslation } from 'react-i18next';
import { formatGetal, isObject, isTelling } from '../format';
import { useDrempelOpslaan, useWatAls } from '../hooks/useInstellingen';
import { weeknummer } from '../week';
import type { WatAls } from '../work-quality.api';
import { Instellingformulier, WERKSOORT_VOLGORDE, naamVan, opVoornaam } from './Instellingformulier';
import type { Effect } from './Instellingformulier';

export interface ThresholdFormProps {
  /** De drempel die nu geldt, zoals de dienst hem teruggaf. */
  huidigeDrempel: number;
  /** De laatst afgesloten week, of leeg zolang er nog geen enkele is. */
  week: string;
  /** Het bereik: `max(20, drempelMinimum)` tot en met `min(100, drempelMaximum)`; de pagina rekent het uit. */
  min: number;
  max: number;
}

/**
 * Het Drempelformulier (story 4.5, herbouwd in story 12, S7): één veld, en eronder wie er met die
 * waarde in de laatst afgesloten week onder de drempel valt, per werksoort apart (M4) en met namen.
 *
 * **Het scherm rekent niets uit** (NFR9, AD-13). Elk getal komt uit het wat-als-antwoord; de enige
 * vergelijkingen hier — kloppen de aantallen onderling — bepalen alleen óf een zin verschijnt. De zin
 * noemt de `drempel` uit het antwoord, niet de getypte waarde.
 */
export function ThresholdForm({ huidigeDrempel, week, min, max }: ThresholdFormProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const opslag = useDrempelOpslaan();
  const getal = (waarde: number) => formatGetal(waarde, locale);

  function effect(antwoord: WatAls): Effect {
    const weekKey = isObject(antwoord.afbakening) ? antwoord.afbakening.weekKey : undefined;
    const w = weeknummer(typeof weekKey === 'string' && weekKey !== '' ? weekKey : week);
    const onder = antwoord.aantalOnderDrempel;
    const totaal = antwoord.aantalCijfers;

    if (!isTelling(onder) || !isTelling(totaal) || !isTelling(antwoord.drempel)) {
      const zin = t('qa.instellingen.gevolgAantallenOntbreken');
      return { inhoud: <p>{zin}</p>, samenvatting: zin };
    }
    // Méér onder de drempel dan er cijfers zijn is geen meetuitkomst maar een fout in het antwoord.
    if (onder > totaal) {
      const zin = t('qa.instellingen.gevolgAantallenKloppenNiet');
      return { inhoud: <p>{zin}</p>, samenvatting: zin };
    }
    if (totaal === 0) {
      const zin = t('qa.instellingen.effectDrempelNiemand', { week: w });
      return { inhoud: <p>{zin}</p>, samenvatting: zin };
    }

    const zin = t('qa.instellingen.effectDrempel', {
      count: onder,
      onder: getal(onder),
      totaal: getal(totaal),
      drempel: getal(antwoord.drempel),
      week: w,
    });
    const per = isObject(antwoord.perWerksoort) ? antwoord.perWerksoort : {};
    const dunste = isObject(antwoord.dunsteGetoondeMeting) ? antwoord.dunsteGetoondeMeting : undefined;

    return {
      samenvatting: zin,
      inhoud: (
        <>
          <p className="font-semibold">{zin}</p>
          {WERKSOORT_VOLGORDE.map(([sleutel, labelSleutel]) => {
            const blok: unknown = per[sleutel];
            // Een werksoort die het antwoord niet (leesbaar) noemt, staat er niet.
            if (!isObject(blok) || !Array.isArray(blok.onderDrempel)) return null;
            const personen = opVoornaam(blok.onderDrempel.filter(isObject), locale);
            return (
              <div className="flex flex-col gap-1" key={sleutel}>
                <h3 className="text-[13px] font-semibold text-[var(--foreground-heading)]">{t(labelSleutel)}</h3>
                {personen.length === 0 ? (
                  <p>{t('qa.instellingen.effectNiemandOnderDrempel')}</p>
                ) : (
                  <ul className="flex list-disc flex-col gap-0.5 pl-5">
                    {personen.map((persoon, plek) => (
                      <li className="break-words" key={`${String(persoon.userId)}-${plek}`}>
                        {isTelling(persoon.aantalBeoordeeld)
                          ? t('qa.instellingen.effectPersoonBeoordeeld', {
                              naam: naamVan(persoon, t),
                              aantal: getal(persoon.aantalBeoordeeld),
                            })
                          : naamVan(persoon, t)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
          {isTelling(dunste?.aantalProducten) ? (
            <p>{t('qa.instellingen.gevolgDunste', { producten: getal(dunste.aantalProducten) })}</p>
          ) : dunste?.reden === 'geen_getoonde_meting' ? (
            // Geen "0 producten": bij deze waarde wordt er helemaal geen meting meer getoond.
            <p>{t('qa.instellingen.gevolgGeenGetoondeMeting')}</p>
          ) : (
            <p>{t('qa.instellingen.gevolgDunsteOnbekend')}</p>
          )}
        </>
      ),
    };
  }

  return (
    <Instellingformulier<WatAls>
      bereikMelding={t('qa.instellingen.drempelBereikMelding', { min: getal(min), max: getal(max) })}
      bevestiging={(drempel) => t('qa.instellingen.drempelOpgeslagen', { drempel: getal(drempel) })}
      effect={effect}
      eenheid={t('qa.instellingen.drempelEenheid')}
      huidigeWaarde={huidigeDrempel}
      kop={t('qa.instellingen.drempelKop')}
      label={t('qa.instellingen.drempelLabel')}
      max={max}
      min={min}
      misluktTekst={t('qa.instellingen.opslaanMislukt')}
      opslag={{ ...opslag, opgeslagen: opslag.opgeslagenDrempel }}
      kind="drempel"
      uitleg={t('qa.instellingen.drempelUitleg')}
      useGevolg={useWatAls}
      week={week}
    />
  );
}
